// Geometry helpers and constants for Google Maps measurement tools

export const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
// 'marker' is required for google.maps.marker.AdvancedMarkerElement (replaces deprecated Marker).
// 'drawing' is intentionally NOT loaded — we use our own CustomDrawingController (the
// drawing library was deprecated by Google in Aug 2025).
export const LIBRARIES = ['places', 'geometry', 'marker'];
// AdvancedMarkerElement requires a mapId on the Map. Customers can configure their own
// Cloud-styled map via REACT_APP_GOOGLE_MAPS_MAP_ID; otherwise we fall back to Google's
// public DEMO_MAP_ID which works out-of-the-box for any project.
export const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';

export const MAP_TYPES = {
  satellite: { id: 'satellite', label: 'Satellite' },
  hybrid: { id: 'hybrid', label: 'Hybrid' },
  roadmap: { id: 'roadmap', label: 'Roadmap' },
  terrain: { id: 'terrain', label: 'Terrain' },
};

export const COLORS = ['#7367F0', '#28C76F', '#FF9F43', '#EA5455', '#00CFE8', '#FF6B6B', '#4ECDC4', '#FFE66D', '#2C3E50', '#FFFFFF'];

// Site Logistics catalogue — keys map to shape_type values, single source of truth.
export const SITE_LOGISTICS = {
  site_crane:    { label: 'Crane',       glyph: 'C',  color: '#FF9F43', hasRadius: false },
  site_office:   { label: 'Site Office', glyph: 'O',  color: '#00CFE8', hasRadius: false },
  site_storage:  { label: 'Storage',     glyph: 'S',  color: '#28C76F', hasRadius: false },
  site_toilet:   { label: 'Toilet',      glyph: 'WC', color: '#7367F0', hasRadius: false },
  site_skipbin:  { label: 'Skip Bin',    glyph: 'B',  color: '#EA5455', hasRadius: false },
};
export function mkDrawOpts(color = '#7367F0', opacity = 0.12) {
  return { fillColor: color, fillOpacity: opacity, strokeColor: color, strokeWeight: 2, editable: false, draggable: true };
}

const G = () => window.google?.maps?.geometry?.spherical;

function polyArea(p) { return G()?.computeArea(p.getPath()) || 0; }
function lineLen(p) { return G()?.computeLength(p.getPath()) || 0; }
function circArea(c) { return Math.PI * c.getRadius() ** 2; }

function rectArea(r) {
  if (r.getBounds) {
    const b = r.getBounds(); if (!b || !G()) return 0;
    const ne = b.getNorthEast(), sw = b.getSouthWest();
    return G().computeDistanceBetween(new window.google.maps.LatLng(sw.lat(), sw.lng()), new window.google.maps.LatLng(sw.lat(), ne.lng()))
      * G().computeDistanceBetween(new window.google.maps.LatLng(sw.lat(), sw.lng()), new window.google.maps.LatLng(ne.lat(), sw.lng()));
  }
  if (r.getPath) return G()?.computeArea(r.getPath()) || 0;
  return 0;
}

function polyPerim(p) {
  if (!G()) return 0;
  const path = p.getPath(); let l = 0;
  for (let i = 0; i < path.getLength(); i++) l += G().computeDistanceBetween(path.getAt(i), path.getAt((i + 1) % path.getLength()));
  return l;
}

function fmtA(v) { return `${v.toFixed(2)} m\u00B2`; }
export function fmtD(v) { return v >= 1000 ? `${(v / 1000).toFixed(2)} km` : `${v.toFixed(2)} m`; }

function getCoords(path) {
  const c = [];
  for (let i = 0; i < path.getLength(); i++) { const p = path.getAt(i); c.push([p.lng(), p.lat()]); }
  return c;
}

function angleBetween(p1, p2, p3) {
  const a = { x: p1.lng() - p2.lng(), y: p1.lat() - p2.lat() };
  const b = { x: p3.lng() - p2.lng(), y: p3.lat() - p2.lat() };
  const dot = a.x * b.x + a.y * b.y;
  const magA = Math.sqrt(a.x ** 2 + a.y ** 2);
  const magB = Math.sqrt(b.x ** 2 + b.y ** 2);
  return Math.acos(Math.max(-1, Math.min(1, dot / (magA * magB)))) * (180 / Math.PI);
}

export function getShapeType(shape) {
  if (shape instanceof window.google.maps.Circle) return 'circle';
  if (shape instanceof window.google.maps.Rectangle) return 'rectangle';
  if (shape instanceof window.google.maps.Polyline && !(shape instanceof window.google.maps.Polygon)) return 'polyline';
  if (isMarker(shape)) {
    if (shape.__siteKind) return shape.__siteKind;
    return 'marker';
  }
  return 'polygon';
}

// =====================================================================
// AdvancedMarkerElement migration helpers
//
// google.maps.Marker is deprecated. We migrate via a thin compat helper:
//  - createMarker({ position, map, draggable, icon, label, title, ... }) returns an
//    AdvancedMarkerElement with .setMap() / .getPosition() / .setPosition() /
//    .setDraggable() shims so the rest of the codebase keeps using the familiar API.
//  - isMarker(shape) replaces `instanceof google.maps.Marker` checks.
// =====================================================================

export function isMarker(shape) {
  if (!shape) return false;
  if (shape.__isAppMarker === true) return true;
  const AME = window.google?.maps?.marker?.AdvancedMarkerElement;
  if (AME && shape instanceof AME) return true;
  return !!window.google?.maps?.Marker && shape instanceof window.google.maps.Marker;
}

function _buildMarkerContent(icon, label) {
  // Build a DOM node from a legacy Marker { icon, label } shape. Returns null when the
  // marker should remain invisible (icon scale 0, no label) — AdvancedMarkerElement still
  // accepts position-only markers fine in that case.
  const hasIcon = icon && typeof icon === 'object';
  const hasLabel = label && (typeof label === 'string' || (label && (label.text || '').length));
  if (!hasIcon && !hasLabel) return null;

  // For pin-style markers (icon.url present), use Google's PinElement — it's the canonical
  // replacement for legacy Marker, handles its own anchoring and renders a recognizable
  // map pin at the correct map size.
  const PinElement = window.google?.maps?.marker?.PinElement;
  if (hasIcon && icon.url && PinElement) {
    const pin = new PinElement({
      background: icon.color || '#EA5455',
      borderColor: '#FFFFFF',
      glyphColor: '#FFFFFF',
      scale: 1.0,
    });
    return pin;
  }

  const wrap = document.createElement('div');
  wrap.style.display = 'inline-flex';
  wrap.style.alignItems = 'center';
  wrap.style.justifyContent = 'center';
  wrap.style.pointerEvents = 'auto';
  wrap.style.whiteSpace = 'nowrap';

  if (hasIcon && icon.path === window.google?.maps?.SymbolPath?.CIRCLE && (icon.scale || 0) > 0) {
    const dot = document.createElement('div');
    const size = (icon.scale || 5) * 2;
    dot.style.width = `${size}px`;
    dot.style.height = `${size}px`;
    dot.style.borderRadius = '50%';
    dot.style.background = icon.fillColor || '#7367F0';
    dot.style.border = `${icon.strokeWeight || 1}px solid ${icon.strokeColor || '#ffffff'}`;
    dot.style.opacity = String(icon.fillOpacity == null ? 1 : icon.fillOpacity);
    dot.style.boxSizing = 'border-box';
    dot.style.boxShadow = '0 1px 3px rgba(0,0,0,0.4)';
    // AdvancedMarkerElement anchors content at bottom-center. Without this translate
    // the dot would sit ABOVE the click point. Shift the wrapper down by half the dot
    // height so the dot's center lands exactly on the LatLng.
    wrap.style.transform = `translateY(${size / 2}px)`;
    wrap.appendChild(dot);
  }

  if (hasLabel) {
    const span = document.createElement('span');
    if (typeof label === 'string') {
      span.textContent = label;
    } else {
      span.textContent = label.text || '';
      span.style.color = label.color || '#000000';
      span.style.fontSize = label.fontSize || '13px';
      span.style.fontWeight = String(label.fontWeight || 'normal');
      if (label.fontFamily) span.style.fontFamily = label.fontFamily;
      if (label.className) span.className = label.className;
    }
    span.style.userSelect = 'none';
    if (hasIcon && icon.path === window.google?.maps?.SymbolPath?.CIRCLE && (icon.scale || 0) > 0) {
      span.style.marginLeft = '4px';
    }
    wrap.appendChild(span);
  }

  return wrap;
}

function _toLatLng(p) {
  if (!p) return null;
  if (typeof p.lat === 'function' && typeof p.lng === 'function') return p; // already LatLng
  if (typeof p.lat === 'number' && typeof p.lng === 'number') {
    return new window.google.maps.LatLng(p.lat, p.lng);
  }
  return null;
}

export function createMarker(opts = {}) {
  const AME = window.google?.maps?.marker?.AdvancedMarkerElement;
  // Fallback to legacy Marker if AdvancedMarkerElement isn't available (rare).
  if (!AME) {
    return new window.google.maps.Marker(opts);
  }
  const { position, map, draggable, icon, label, title, zIndex, clickable } = opts;
  const content = _buildMarkerContent(icon, label);
  const marker = new AME({
    position,
    map,
    title,
    zIndex,
    gmpDraggable: !!draggable,
    gmpClickable: clickable !== false,
    content,
  });
  // Mark for isMarker() checks.
  marker.__isAppMarker = true;
  // Legacy-API compat shims — only add when not present on the prototype, otherwise
  // we would shadow Google's own implementation and create infinite recursion through
  // the `map` / `position` property setters.
  if (typeof marker.setMap !== 'function') {
    marker.setMap = function (m) { this.map = m; };
  }
  if (typeof marker.getMap !== 'function') {
    marker.getMap = function () { return this.map; };
  }
  if (typeof marker.setPosition !== 'function') {
    marker.setPosition = function (p) { this.position = p; };
  }
  if (typeof marker.getPosition !== 'function') {
    marker.getPosition = function () { return _toLatLng(this.position); };
  }
  if (typeof marker.setDraggable !== 'function') {
    marker.setDraggable = function (d) { this.gmpDraggable = !!d; };
  }
  if (typeof marker.getDraggable !== 'function') {
    marker.getDraggable = function () { return !!this.gmpDraggable; };
  }
  if (typeof marker.setTitle !== 'function') {
    marker.setTitle = function (t) { this.title = t; };
  }
  return marker;
}

// Site-logistics marker built with PinElement — coloured pin with a letter glyph.
// Each marker is also tagged with __siteKind so MapView can identify them after restore.
export function createSiteMarker(kind, opts = {}) {
  const cfg = SITE_LOGISTICS[kind];
  if (!cfg) return null;
  const AME = window.google?.maps?.marker?.AdvancedMarkerElement;
  const PinElement = window.google?.maps?.marker?.PinElement;
  if (!AME || !PinElement) {
    return createMarker({ ...opts, icon: { url: 'pin', color: cfg.color }, label: cfg.glyph });
  }
  const pin = new PinElement({
    background: cfg.color,
    borderColor: '#FFFFFF',
    glyphText: cfg.glyph,
    glyphColor: '#FFFFFF',
    scale: 1.15,
  });
  const marker = new AME({
    position: opts.position,
    map: opts.map,
    title: opts.title || cfg.label,
    zIndex: opts.zIndex,
    gmpDraggable: !!opts.draggable,
    gmpClickable: opts.clickable !== false,
    content: pin,
  });
  marker.__isAppMarker = true;
  marker.__siteKind = kind;
  // Legacy shims kept for environments where AdvancedMarkerElement does not
  // expose the classic Marker setMap/getMap/setPosition/etc. API (older
  // mainstream channels mostly already provide these, but our test harness
  // and a couple of customer browsers historically did not).
  if (typeof marker.setMap !== 'function') {
    marker.setMap = function (m) { this.map = m; };
  }
  if (typeof marker.getMap !== 'function') marker.getMap = function () { return this.map; };
  if (typeof marker.setPosition !== 'function') marker.setPosition = function (p) { this.position = p; };
  if (typeof marker.getPosition !== 'function') marker.getPosition = function () { return _toLatLng(this.position); };
  if (typeof marker.setDraggable !== 'function') marker.setDraggable = function (d) { this.gmpDraggable = !!d; };
  if (typeof marker.getDraggable !== 'function') marker.getDraggable = function () { return !!this.gmpDraggable; };
  if (typeof marker.setTitle !== 'function') marker.setTitle = function (t) { this.title = t; };
  return marker;
}

export function buildMeasurement(shape, type, num, color) {
  let mt = '', st = '', value = 0, unit = '', label = '', details = '', coords = [], radius;
  if (type === 'polygon') {
    const a = polyArea(shape), pe = polyPerim(shape); coords = getCoords(shape.getPath());
    mt = 'area'; st = 'polygon'; value = a; unit = 'sq m'; label = `Polygon ${num}`;
    details = `Type: roof_area\nArea: ${fmtA(a)}\nPerimeter: ${fmtD(pe)}`;
  } else if (type === 'rectangle') {
    // After rotation, rectangle becomes a polygon — handle both cases
    if (shape.getBounds) {
      const b = shape.getBounds(), a = rectArea(shape), ne = b.getNorthEast(), sw = b.getSouthWest();
      const w = G().computeDistanceBetween(new window.google.maps.LatLng(sw.lat(), sw.lng()), new window.google.maps.LatLng(sw.lat(), ne.lng()));
      const h = G().computeDistanceBetween(new window.google.maps.LatLng(sw.lat(), sw.lng()), new window.google.maps.LatLng(ne.lat(), sw.lng()));
      coords = [[sw.lng(), sw.lat()], [ne.lng(), ne.lat()]]; mt = 'area'; st = 'rectangle'; value = a; unit = 'sq m'; label = `Rectangle ${num}`;
      details = `Type: roof_area\nArea: ${fmtA(a)}\nW: ${fmtD(w)} x H: ${fmtD(h)}`;
    } else if (shape.getPath) {
      const a = polyArea(shape); coords = getCoords(shape.getPath());
      const path = shape.getPath();
      let w = 0, h = 0;
      if (path.getLength() >= 4) {
        w = G().computeDistanceBetween(path.getAt(0), path.getAt(1));
        h = G().computeDistanceBetween(path.getAt(0), path.getAt(3));
      }
      mt = 'area'; st = 'rectangle'; value = a; unit = 'sq m'; label = `Rectangle ${num}`;
      details = `Type: roof_area\nArea: ${fmtA(a)}\nW: ${fmtD(w)} x H: ${fmtD(h)}`;
    }
  } else if (type === 'circle') {
    const c = shape.getCenter(), r = shape.getRadius(), a = circArea(shape);
    coords = [[c.lng(), c.lat()]]; radius = r; mt = 'area'; st = 'circle'; value = a; unit = 'sq m'; label = `Circle ${num}`;
    details = `Type: roof_area\nArea: ${fmtA(a)}\nRadius: ${fmtD(r)}`;
  } else if (type === 'polyline' || type === 'arrow' || type === 'multi_distance') {
    const d = lineLen(shape); coords = getCoords(shape.getPath());
    mt = 'distance'; st = type; value = d; unit = 'm'; label = `${type === 'arrow' ? 'Arrow' : type === 'multi_distance' ? 'Multi-Dist' : 'Line'} ${num}`;
    if (type === 'multi_distance' && coords.length > 2) {
      let segs = []; let total = 0;
      for (let i = 0; i < coords.length - 1; i++) {
        const sd = G().computeDistanceBetween(new window.google.maps.LatLng(coords[i][1], coords[i][0]), new window.google.maps.LatLng(coords[i + 1][1], coords[i + 1][0]));
        segs.push(`Seg ${i + 1}: ${fmtD(sd)}`); total += sd;
      }
      details = `Type: multi_distance\n${segs.join('\n')}\nTotal: ${fmtD(total)}`;
    } else { details = `Type: ${type}\nLength: ${fmtD(d)}`; }
  } else if (type === 'perimeter') {
    const pe = polyPerim(shape); coords = getCoords(shape.getPath());
    mt = 'perimeter'; st = 'perimeter'; value = pe; unit = 'm'; label = `Perimeter ${num}`;
    details = `Type: perimeter_only\nPerimeter: ${fmtD(pe)}\nPoints: ${coords.length}`;
  } else if (type === 'marker') {
    const pos = shape.getPosition(); coords = [[pos.lng(), pos.lat()]];
    mt = 'marker'; st = 'marker'; label = `Marker ${num}`;
    details = `Type: point\nLat: ${pos.lat().toFixed(6)}\nLng: ${pos.lng().toFixed(6)}`;
  } else if (type === 'angle') {
    coords = getCoords(shape.getPath());
    if (coords.length === 3) {
      const p1 = new window.google.maps.LatLng(coords[0][1], coords[0][0]);
      const p2 = new window.google.maps.LatLng(coords[1][1], coords[1][0]);
      const p3 = new window.google.maps.LatLng(coords[2][1], coords[2][0]);
      const angle = angleBetween(p1, p2, p3);
      value = angle; mt = 'angle'; st = 'angle'; label = `Angle ${num}`;
      details = `Type: angle\nAngle: ${angle.toFixed(1)}\u00B0\nVertex: ${coords[1][1].toFixed(6)}, ${coords[1][0].toFixed(6)}`;
    }
  } else if (type === 'height') {
    coords = getCoords(shape.getPath());
    if (coords.length === 2) {
      const d = G().computeDistanceBetween(new window.google.maps.LatLng(coords[0][1], coords[0][0]), new window.google.maps.LatLng(coords[1][1], coords[1][0]));
      const estH = d * 2.5;
      value = estH; mt = 'height'; st = 'height'; label = `Height Est. ${num}`;
      details = `Type: height_estimate\nShadow Length: ${fmtD(d)}\nEst. Height: ${fmtD(estH)}`;
    }
  } else if (type === 'text_label') {
    const pos = shape.getPosition(); coords = [[pos.lng(), pos.lat()]];
    mt = 'text'; st = 'text_label'; label = `Label ${num}`;
    details = `Type: annotation\nLat: ${pos.lat().toFixed(6)}\nLng: ${pos.lng().toFixed(6)}`;
  } else if (type && type.startsWith('site_')) {
    const cfg = SITE_LOGISTICS[type];
    const kindLabel = cfg?.label || 'Site item';
    const pos = shape.getPosition ? shape.getPosition() : null;
    if (pos) coords = [[pos.lng(), pos.lat()]];
    mt = 'site_logistics'; st = type; label = `${kindLabel} ${num}`;
    details = `Type: site_logistics\nKind: ${kindLabel}` + (pos ? `\nLat: ${pos.lat().toFixed(6)}\nLng: ${pos.lng().toFixed(6)}` : '');
  }
  return { measurement_type: mt, shape_type: st, coordinates: coords, value, unit, label, details, description: '', radius, color: color || '#7367F0', opacity: 0.12, locked: false, created_at: new Date().toISOString() };
}
